import { useState } from 'react';
import { Route, RoutePerson } from '../../../@types/routes';
import { User } from '../../../@types/users';
import { ActionButton } from '../../../components/action-button';
import { Modal } from '../../../components/modal';
import { PeopleSelector } from '../../../components/people-selector';
import { SaveRoutePayload } from '../../../queries/route';
import { getPersonName } from '../../../utils/routes';
import { AddUserModal } from '../../users/add-user-modal';
import {
  ButtonsRow,
  CustomInput,
  ErrorMessage,
} from './style';

type EditRouteModalProps = {
  route: Route;
  onSaveClick: (payload: SaveRoutePayload) => void;
  onCancel: () => void;
};

export const EditRouteModal = ({ route, onSaveClick, onCancel }: EditRouteModalProps) => {
  const [routeName, setRouteName] = useState<string>(route.name);

  // Single driver selection (only 1 driver)
  const [driver, setDriver] = useState<RoutePerson | null>(route.driver || null);

  // Multiple passengers selection
  const [passengers, setPassengers] = useState<RoutePerson[]>(route.passengers || []);

  const [errorMessage, setErrorMessage] = useState('');

  // Add User Modal State
  const [addUserModalConfig, setAddUserModalConfig] = useState<{
    initialFirstName: string;
    initialLastName: string;
    initialIsDriver: boolean;
    target: 'driver' | 'passenger';
  } | null>(null);

  const handleRouteNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRouteName(event.currentTarget.value);
  };

  const handleRouteDriverChange = (user: User) => {
    setDriver({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      isLegacy: false,
    });
  };

  const handleRemoveDriver = () => {
    setDriver(null);
  };

  const handleSelectPassenger = (user: User) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim().toLowerCase();
    if (
      passengers.some(
        (p) =>
          p.id === user.id ||
          getPersonName(p).toLowerCase() === fullName,
      )
    ) {
      return;
    }
    setPassengers((prev) => [
      ...prev,
      {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        isLegacy: false,
      },
    ]);
  };

  const handleRemovePassenger = (index: number) => {
    setPassengers((prev) => prev.filter((_, i) => i !== index));
  };

  const parseNameQuery = (query: string) => {
    const parts = query.trim().split(/\s+/);
    if (parts.length > 1) {
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' '),
      };
    }

    return {
      firstName: query.trim(),
      lastName: '',
    };
  };

  const handleAddNewDriverRequest = (query: string) => {
    const { firstName, lastName } = parseNameQuery(query);
    setAddUserModalConfig({
      initialFirstName: firstName,
      initialLastName: lastName,
      initialIsDriver: true,
      target: 'driver',
    });
  };

  const handleAddNewPassengerRequest = (query: string) => {
    const { firstName, lastName } = parseNameQuery(query);
    setAddUserModalConfig({
      initialFirstName: firstName,
      initialLastName: lastName,
      initialIsDriver: false,
      target: 'passenger',
    });
  };

  const handleUserCreated = (createdUser: User) => {
    const newPerson: RoutePerson = {
      id: createdUser.id,
      firstName: createdUser.firstName,
      lastName: createdUser.lastName,
      isLegacy: false,
    };
    if (addUserModalConfig?.target === 'driver') {
      setDriver(newPerson);
    } else if (addUserModalConfig?.target === 'passenger') {
      setPassengers((prev) => [...prev, newPerson]);
    }
    setAddUserModalConfig(null);
  };

  const handleSaveClick = () => {
    setErrorMessage('');

    if (!routeName.trim()) {
      setErrorMessage('שם המסלול לא יכול להיות ריק');

      return;
    }

    const driverName = getPersonName(driver);
    const driverId = driver?.id || null;

    const passengerNames = passengers.map(getPersonName).filter(Boolean).join(', ');
    const passengerIds = passengers
      .map((p) => p.id)
      .filter((id): id is string => typeof id === 'string' && id !== '');

    onSaveClick({
      routeId: route.id,
      name: routeName.trim(),
      driver: driverName,
      driverId,
      passengers: passengerNames,
      passengerIds,
    });
  };

  return (
    <>
      <Modal title="פרטי המסלול" isVisible onClose={onCancel}>
        <form
          onSubmit={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handleSaveClick();
          }}
        >
          <CustomInput label="שם המסלול" value={routeName} onChange={handleRouteNameChange} />

          {/* DRIVER (Single Selection) */}
          <PeopleSelector
            label="שם הנהג/ת"
            mode="single"
            selected={driver ? [driver] : []}
            onRemoveItem={handleRemoveDriver}
            backendFilters={{ isDriver: true }}
            onSelectItem={handleRouteDriverChange}
            onAddNewRequest={handleAddNewDriverRequest}
          />

          {/* PASSENGERS (Multi Selection) */}
          <PeopleSelector
            label="שמות הנוסעים"
            mode="multiple"
            selected={passengers}
            onRemoveItem={(_person, index) => handleRemovePassenger(index)}
            onSelectItem={handleSelectPassenger}
            onAddNewRequest={handleAddNewPassengerRequest}
          />

          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <ButtonsRow>
            <ActionButton title="שמירה" type="submit" />
            <ActionButton title="ביטול" onClick={onCancel} kind="secondary" />
          </ButtonsRow>
        </form>
      </Modal>

      {addUserModalConfig && (
        <AddUserModal
          initialFirstName={addUserModalConfig.initialFirstName}
          initialLastName={addUserModalConfig.initialLastName}
          initialIsDriver={addUserModalConfig.initialIsDriver}
          onUserCreated={handleUserCreated}
          onCancel={() => setAddUserModalConfig(null)}
        />
      )}
    </>
  );
};
